import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, tap, withLatestFrom } from 'rxjs/operators';

import { CaseTypeEditorEffects } from '../../../effects/case-type-editor.effects';
import {
  LabelConsolidationFailureEffects,
  LabelFailureEvent,
} from '../../../effects/label-consolidation-failure.effects';
import { CaseTypeEditorState } from '../../../state/case-type-editor.state';
import { LabelConfigState } from '../../../state/label-config.state';
import { LabelState } from '../../../state/label.state';
import { StagedCaseDataState } from '../../../state/staged-case-data.state';
import { StagedMethodTypesState } from '../../../state/staged-method-types.state';
import { StagedPipelinesState } from '../../../state/staged-pipelines.state';
import { StagedResourcesState } from '../../../state/staged-resources.state';

/**
 * Component facade for the case-type editor: owns the row draft (name +
 * description) and the save/cancel validation handoff. Trimmed mirror of
 * `MethodTypeEditorFacade` — no asset type / sample names / config. Load
 * orchestration and the protocol-cascade trigger belong to the route-host
 * facade (`CaseTypeEditorPageFacade`).
 */
@Injectable()
export class CaseTypeEditorFacade {
  constructor(
    private readonly editorEffects: CaseTypeEditorEffects,
    private readonly state: CaseTypeEditorState,
    private readonly stagedCaseData: StagedCaseDataState,
    private readonly stagedMethodTypes: StagedMethodTypesState,
    private readonly labelState: LabelState,
    private readonly labelConfigState: LabelConfigState,
    private readonly stagedPipelinesState: StagedPipelinesState,
    private readonly stagedResourcesState: StagedResourcesState,
    private readonly labelFailureEffects: LabelConsolidationFailureEffects
  ) {}

  // ============================================================================
  // Selectors — passthrough
  // ============================================================================

  public readonly name$ = this.state.name$;
  public readonly description$ = this.state.description$;
  public readonly nameError$ = this.state.nameError$;
  public readonly loading$ = this.state.loading$;

  /**
   * In-flight signal for the save cascade as seen from the editor: EVERY phase
   * the case-type flow runs owns a `saving` slice — the case-type row, the
   * staged data fields, the staged method-type loop, the label batch, the
   * staged-pipeline batch, the resource walk and the config tail
   * (`MethodTypeEditorFacade.saving$` approach, widened to the case-type
   * flow's phases). `SaveProtocolCascadeEffects` is deliberately NOT injected —
   * stories omit it; the slices carry the signal.
   */
  public readonly saving$: Observable<boolean> = combineLatest([
    this.state.saving$,
    this.stagedCaseData.saving$,
    this.stagedMethodTypes.saving$,
    this.labelState.saving$,
    this.stagedPipelinesState.saving$,
    this.stagedResourcesState.saving$,
    this.labelConfigState.saving$,
  ]).pipe(
    map((flags) => flags.some(Boolean)),
    distinctUntilChanged()
  );

  /**
   * Whether cancelling now would discard user work — row edits, data-field
   * changes, method-type assignments or staged
   * method-type/pipeline/resource/label drafts. Compares the effects' live
   * session signature against the baseline captured at seed. False until a
   * seed completes (`baselineSignature` still `null`), so a failed load never
   * warns.
   */
  public readonly hasChanges$: Observable<boolean> = this.state.select(
    this.editorEffects.currentSignature$,
    this.state.baselineSignature$,
    (current, baseline) => baseline !== null && current !== baseline
  );

  // ============================================================================
  // Selectors — error dispatch
  // ============================================================================

  /**
   * Unified error stream for the page banner. Load failures win, then the
   * case-type row save failure, then the first staged method-type row failure,
   * then the first staged-pipeline row failure, then the first resource row
   * failure (the cascade is fail-fast, so at most one batch holds exceptions).
   * Label and label-config row failures surface as snackbars instead
   * (`labelSaveFailed$` / `labelConfigSaveFailed$`, method-type-editor parity).
   * `StagedCaseDataState.saveException$` is deliberately not read — the cascade
   * records the same row-save error on `CaseTypeEditorState`. Effects/cascade
   * never write i18n keys; they record typed exceptions on the owner states and
   * the facade dispatches them into its own namespace here.
   */
  public readonly error$: Observable<string | null> = combineLatest([
    this.state.loadException$,
    this.state.saveException$,
    this.stagedMethodTypes.saveExceptions$,
    this.stagedPipelinesState.saveExceptions$,
    this.stagedResourcesState.saveExceptions$,
  ]).pipe(
    map(([loadException, saveException, methodTypeExceptions, pipelineExceptions, resourceExceptions]) => {
      if (loadException) return 'case_type_editor.errors.load_failed';
      if (saveException) return 'case_type_editor.errors.save_failed';
      if (Object.keys(methodTypeExceptions).length > 0) return 'case_type_editor.errors.method_type_save_failed';
      if (Object.keys(pipelineExceptions).length > 0) return 'case_type_editor.errors.pipeline_save_failed';
      if (Object.keys(resourceExceptions).length > 0) return 'case_type_editor.errors.resource_save_failed';
      return null;
    }),
    distinctUntilChanged()
  );

  // ============================================================================
  // Selectors — per-row label failure events (method-type-editor parity)
  // ============================================================================

  /**
   * Each failing label-save emits a `{ name, error }` pair to toast (shared
   * `LabelConsolidationFailureEffects` derivation — display name already
   * resolved in the current app language).
   */
  public readonly labelSaveFailed$: Observable<LabelFailureEvent> = this.labelFailureEffects.labelSaveFailed$;

  /** Per-row label-config tail failures — see `labelSaveFailed$`. */
  public readonly labelConfigSaveFailed$: Observable<LabelFailureEvent> =
    this.labelFailureEffects.labelConfigSaveFailed$;

  // ============================================================================
  // Commands
  // ============================================================================

  public readonly setName = this.state.setName;
  public readonly setDescription = this.state.setDescription;

  /**
   * Save is validate-then-handoff: the button is never disabled, so every click
   * re-runs the required-name check (no DB uniqueness check — method-type
   * editor parity). Only a non-blank name clears the error and emits the
   * one-shot `saved` outcome; persistence belongs to the consumer (the page
   * runs the protocol cascade on `saved`).
   */
  public readonly save = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.state.name$),
      tap(([, name]: [void, string]) => {
        if (name.trim() === '') {
          this.state.setNameError('case_type_editor.errors.name_required');
          return;
        }
        this.state.setNameError(null);
        this.state.confirm({ kind: 'saved' });
      })
    )
  );

  /**
   * Cancel is a pure handoff: emit the one-shot `cancelled` outcome on the
   * shared state. The COMPONENT runs the discard-confirmation dialog first; the
   * consumer (route page facade) decides what to do.
   */
  public readonly cancel = this.state.effect<void>((trigger$) =>
    trigger$.pipe(tap(() => this.state.confirm({ kind: 'cancelled' })))
  );
}
